import {
  AwsRum,
  JsErrorPlugin,
  NavigationPlugin,
  ResourcePlugin,
  WebVitalsPlugin,
} from '@aws-rum/web-slim';

import type { AwsRumConfig } from './awsRumConfig';
import {
  browserErrorDetailsFromEvent,
  browserErrorDetailsFromThrown,
  shouldIgnoreBrowserError,
} from './browserErrorFilters';

// CloudWatch RUM (Real User Monitoring) browser client.
//
// https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch-RUM.html
// https://github.com/aws-observability/aws-rum-web/tree/main/docs

/**
 * Decides which sampled telemetries to record for the current page load, given a random
 * number in the range [0, 1).
 *
 * Resource timings are recorded only for page loads that are also sampled for web vitals
 * (so that we get the complete page load timing picture for those page loads).
 */
export const sampleTelemetries = (
  config: Pick<
    AwsRumConfig,
    'webVitalsSampleRate' | 'resourceTimingsSampleRate'
  >,
  random: number
) => ({
  webVitals: random < config.webVitalsSampleRate,
  resourceTimings:
    random <
    Math.min(config.resourceTimingsSampleRate, config.webVitalsSampleRate),
});

type ResourceTypes = NonNullable<
  ConstructorParameters<typeof ResourcePlugin>[0]
>['sampleTypes'];

/** All resource types known by the RUM client */
const allResourceTypes = [
  'document',
  'script',
  'stylesheet',
  'font',
  'image',
  'other',
] as ResourceTypes;

let rumClient: AwsRum | undefined;

/**
 * Initializes the RUM client. Should be called once, as early as possible on page load.
 *
 * @param releaseId ID of the release (build) that is running, for source-mapping error stack traces
 */
const init = (config: AwsRumConfig, releaseId: string | undefined) => {
  try {
    const sampling = sampleTelemetries(config, Math.random());

    rumClient = new AwsRum(
      config.appMonitorId,
      releaseId ?? 'unknown',
      config.region,
      {
        // Record all sessions (i.e. page loads) so that we get all errors and custom events.
        // Sampling of the more voluminous telemetries is done by choosing which plugins to load.
        sessionSampleRate: 1,
        // Don't use cookies, so that we don't need cookie consent. This means that each
        // full page load starts a new session.
        allowCookies: false,
        sessionEventLimit: config.sessionEventLimit,
        releaseId,
        eventPluginsToLoad: [
          new JsErrorPlugin({
            ignore: (event) =>
              shouldIgnoreBrowserError(browserErrorDetailsFromEvent(event)),
          }),
          ...(sampling.webVitals
            ? [new NavigationPlugin(), new WebVitalsPlugin()]
            : []),
          ...(sampling.resourceTimings
            ? [
                new ResourcePlugin({
                  eventLimit: config.resourceTimingsLimit,
                  // Apply `eventLimit` to all resource types:
                  recordAllTypes: [],
                  sampleTypes: allResourceTypes,
                }),
              ]
            : []),
        ],
      }
    );
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
};

/** Records an error, unless it matches our error filters */
const recordError = (error: unknown) => {
  if (!shouldIgnoreBrowserError(browserErrorDetailsFromThrown(error))) {
    rumClient?.recordError(error);
  }
};

/** Records a custom event */
const recordEvent = (eventType: string, eventData: object) => {
  rumClient?.recordEvent(eventType, eventData);
};

export const awsRum = {
  init,
  recordError,
  recordEvent,
};
